import {validate, collectFormValues} from 'validate.js';
import {cleanupValidationErrors} from '../util/AppUtil';
import {EMAIL_INVALID_CHARACTERS_REGX} from '../util/AppConstant';
import {
    FORM_INPUT_ON_CHANGE,
    FORM_ON_ERRORS,
    FORM_RESET_STATE,
    FORM_CLEAR_ERRORS,
    FORM_PROCESSING,
    FORM_SET_GLOBAL_SETTINGS,
    FORM_SET_ERROR_ORIGIN,
    SET_TEMPORARY_RESET_PASSWORD_DATA,
    SAVE_PASSWORD_STRENGTH
    
} from './ActionTypes';
import {OmnitureUtil} from '../util/OmnitureUtil';

/**
 * Set store.form.processing true/false
 * @param processing
 * @returns {{type: string, processing: boolean}
 */
export function formProcessing(processing) {
    return { type: FORM_PROCESSING, processing };
}

/**
 * Clear the store.form.errors object
 * @returns {{type: string}}
 */
export function clearErrors() {
    return { type: FORM_CLEAR_ERRORS };
}

/**
 * Sets the store.form.errors object
 * @param errors
 * @returns {{type: string, errors: *}}
 */
export function onFormErrors(errors) {
    return { type: FORM_ON_ERRORS, errors };
}

/**
 * Updates store.form.inputName value
 * @param e
 * @returns {{type: string, e: *}}
 */
export function onInputChange(e) {
    return { type: FORM_INPUT_ON_CHANGE, e };
}

/**
 * save password score to store
 * @param {*} globalSettings 
 */

 export function savePasswordStrength(score) {
     return {
         type: SAVE_PASSWORD_STRENGTH,
         payload: score
     }
 }

/**
 * Updates store.form.globalSettings object
 * @param {*} globalSettings 
 * @returns {{type: string, globalSettings: *}}
 */
export function setGlobalSettings(globalSettings) {
    return { type: FORM_SET_GLOBAL_SETTINGS, globalSettings };
}


/**
 * Resets the state of store.form
 * @returns {{type: string}}
 */
export function resetState() {
    return { type: FORM_RESET_STATE };
}

/**
 * Set formErrorOrigin
 * @param {*} formErrorOrigin 
 */
export function setFormErrorOrigin(formErrorOrigin) {
    return { type: FORM_SET_ERROR_ORIGIN, formErrorOrigin };
}

export const setTemporaryResetPasswordData = (email) => {
    return {
        type: SET_TEMPORARY_RESET_PASSWORD_DATA,
        payload: email
    }
}

function getFormValueByName(form, name) {
    const { elements } = form;
    for (let len = elements.length, i = 0; i < len; i++) {
        if (elements[i].name === name) {
            return elements[i].value;
        }
    }
    return null;
}

/**
 * Validates an HTML form element with the given validation constraints
 * @param form
 * @param validationConstraints
 * @param validateCallBack
 * @returns {Function}
 */
export function validateForm(form, validationConstraints, validateCallBack, page, logDescription) {
    return (dispatch, getState) => {
        // Set form processing state
        dispatch(formProcessing(true));
        dispatch(clearErrors());

        let {sanitizedForm, validations, restore} = sanitizeFormInputs(form, validationConstraints);
        let errors = null;
        if(sanitizedForm && typeof sanitizedForm.costcoMembNumber === "number") {
            sanitizedForm.costcoMembNumber = String(sanitizedForm.costcoMembNumber);
        }
        // USER-5178: Blocked user from signing up with email having character %
        for (const key in validationConstraints) {
            const constraint = validationConstraints[key];
            const { email } = constraint;
            if (email) {
                const { customValidation, message, signUpValidation = false } = email;
                const checkError = !errors || !errors[key];
                if (signUpValidation) {
                    const formValue = getFormValueByName(form, key).trim();
                    const lastIndex = formValue.lastIndexOf('.');
                    const substringAfterLastDot = formValue.substring(lastIndex + 1);
        
                    // Check if the substring has at least two characters
                    if (substringAfterLastDot.length < 2) {
                        errors = errors || {};
                        errors[key] = [message.replace('^', '')];
                    }                    
                }
                
                if (customValidation && message && checkError) {
                    const formValue = getFormValueByName(form, key);
                    if (formValue && formValue.match(EMAIL_INVALID_CHARACTERS_REGX)) {
                        errors = errors || {};
                        errors[key] = [message.replace('^', '')];
                    }
                }
            }
        }

        // Validate the form with the validation constraints and respond accordingly
        const formErrors = cleanupValidationErrors(validate(sanitizedForm, validations) || null);
        errors = errors ? Object.assign(errors, formErrors) : formErrors;
        
        dispatch(onFormErrors(errors));
       
        restore();

        if (errors === null) {
            validateCallBack(sanitizedForm);
        } else {
            // As common behavior, we submit the form validation errors to Adobe Omniture
            OmnitureUtil.trackFormErrors(errors, logDescription);
            dispatch(formProcessing(false));
        }
    }
}

/**
 * Trims the form input values if {trim: true} is in the validation constraints.
 * @param {*} form 
 * @param {*} validationConstraints 
 */
function sanitizeFormInputs(form, validationConstraints) {

    if(validationConstraints && validationConstraints.sanitizeForm) {
        // collectFormValues() from validate.js helps to sanitize form values.
        const sanitizedForm = collectFormValues(form, validationConstraints.sanitizeForm);
        form = collectFormValues(form);

        // USER-5058: Stage- Trim leading and trailing spaces from email address during sign in
        // Issues: validate.js npm package can only trim all fields in the form or none.
        // Workaround: restore original value if autoTrim (custom property under format) is false
        // More Info: See the nullify and trim options validate.collectFormValues (https://validatejs.org/#utilities-collect-form-values)
        for (const key in sanitizedForm) {
            const constraint = validationConstraints[key];
            if (constraint && constraint.format && !constraint.format.autoTrim) {
                sanitizedForm[key] = form[key];
            }
        }

        let sanitizeForm = validationConstraints.sanitizeForm;
        delete validationConstraints.sanitizeForm;

        return {
            'sanitizedForm': sanitizedForm,
            'validations': validationConstraints,
            'restore': () => {
                validationConstraints.sanitizeForm = sanitizeForm;
            }
        }
    } else {
        return {
            'sanitizedForm': form,
            'validations': validationConstraints,
            'restore': () => {}
        }
    }
    
}
